/**
 * useInfiniteVideos — feed paginado por keyset via React Query.
 * Reusa o padrão de useInfiniteQuery do feed de posts: queryKey por (sort, topic),
 * cursor opaco no pageParam, persistido pelo QueryProvider.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchVideos, fetchUserVideos } from '../services/communityVideosService';
import { videoKeys, VIDEO_PAGE_SIZE, VIDEO_SORT } from '../constants';

export const useInfiniteVideos = ({ sort = VIDEO_SORT.RECENT, section = 'updates', topic = null, enabled = true } = {}) =>
  useInfiniteQuery({
    queryKey: videoKeys.feed(section, sort, topic),
    queryFn: ({ pageParam = null }) =>
      fetchVideos({ sort, section, topic, cursor: pageParam, limit: VIDEO_PAGE_SIZE }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    // Relevância muda devagar (mais cache); recência é mais volátil.
    staleTime: sort === VIDEO_SORT.RELEVANT ? 5 * 60 * 1000 : 60 * 1000,
    enabled,
  });

/**
 * Vídeos de um autor (aba "Posts" do perfil), paginado por offset.
 * Mesmo formato de página ({ items, hasMore }) → reusa selectVideoItems.
 */
export const useUserVideos = (userId, enabled = true) =>
  useInfiniteQuery({
    queryKey: videoKeys.userVideos(userId),
    queryFn: ({ pageParam = 0 }) =>
      fetchUserVideos({ userId, limit: VIDEO_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.hasMore ? allPages.length * VIDEO_PAGE_SIZE : undefined,
    staleTime: 60 * 1000,
    enabled: enabled && !!userId,
  });

/** Achata as páginas em CommunityVideo[] para a UI. */
export const selectVideoItems = (data) => data?.pages?.flatMap((page) => page.items ?? []) ?? [];
